import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api';
import {
  Flag,
  RefreshCw,
  Search,
  Clock,
  CheckCircle,
  XCircle,
  Eye,
  AlertTriangle,
  X,
  KeyRound,
  ChevronRight,
  User as UserIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import ChipBankTabs from '../components/ChipBankTabs';

// ----------------------------------------------------------------
// Disputes — Phase 1.5 admin queue
// ----------------------------------------------------------------
// Players file disputes from the mobile app against transactions they
// don't recognize. Cage management reads them here, takes the status
// through reviewing → resolved/rejected, and writes a resolution note.
//
// All mutations require a supervisor cage PIN.

const fmtCents = (cents) => {
  if (cents == null) return '—';
  return (Number(cents) / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
};

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString();
};

const fmtAge = (iso) => {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
};

const STATUS_TABS = [
  { key: '', label: 'Actionable', icon: Flag },
  { key: 'open', label: 'Open', icon: AlertTriangle },
  { key: 'reviewing', label: 'Reviewing', icon: Eye },
  { key: 'resolved', label: 'Resolved', icon: CheckCircle },
  { key: 'rejected', label: 'Rejected', icon: XCircle },
  { key: 'all', label: 'All', icon: null },
];

export default function Disputes() {
  const [employeeNumber, setEmployeeNumberState] = useState(
    () =>
      (typeof localStorage !== 'undefined' &&
        localStorage.getItem('cage.employeeNumber')) ||
      ''
  );
  const setEmployeeNumber = (v) => {
    setEmployeeNumberState(v);
    if (typeof localStorage !== 'undefined') {
      if (v) localStorage.setItem('cage.employeeNumber', v);
      else localStorage.removeItem('cage.employeeNumber');
    }
  };

  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    open: 0,
    reviewing: 0,
    resolved: 0,
    rejected: 0,
    actionable: 0,
  });

  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [pinModal, setPinModal] = useState(null);

  // ----- fetchers -----
  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/admin/cage/disputes/stats/summary');
      setCounts(res.data.counts);
    } catch (e) {
      // non-fatal
    }
  }, []);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();
      const res = await api.get('/admin/cage/disputes', { params });
      setDisputes(res.data.disputes);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load disputes');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchQuery]);

  const fetchDetail = useCallback(async (id) => {
    setDetailLoading(true);
    try {
      const res = await api.get(`/admin/cage/disputes/${id}`);
      setDetail(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Failed to load dispute');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary();
    fetchList();
  }, [fetchSummary, fetchList]);

  useEffect(() => {
    if (selectedId) fetchDetail(selectedId);
    else setDetail(null);
  }, [selectedId, fetchDetail]);

  // Refetch list + summary every 30s while on the page
  useEffect(() => {
    const id = setInterval(() => {
      fetchSummary();
      fetchList();
      if (selectedId) fetchDetail(selectedId);
    }, 30000);
    return () => clearInterval(id);
  }, [fetchSummary, fetchList, fetchDetail, selectedId]);

  // ----- actions -----
  const startReview = () => {
    if (!detail?.dispute) return;
    setPinModal({
      title: 'Start review',
      description:
        'Flag this dispute as under review. You can come back later to resolve or reject.',
      submitLabel: 'Start review',
      requireNote: false,
      action: async ({ pin, note }) => {
        await api.patch(`/admin/cage/disputes/${detail.dispute.id}`, {
          status: 'reviewing',
          resolutionNote: note || undefined,
          employeeNumber,
          staffPin: pin,
        });
        toast.success('Marked as reviewing');
      },
    });
  };

  const resolve = () => {
    if (!detail?.dispute) return;
    setPinModal({
      title: 'Resolve dispute',
      description:
        'Confirm the dispute has been investigated and any required corrections made. Resolution note is required.',
      submitLabel: 'Resolve',
      requireNote: true,
      noteLabel: 'Resolution note',
      action: async ({ pin, note }) => {
        await api.patch(`/admin/cage/disputes/${detail.dispute.id}`, {
          status: 'resolved',
          resolutionNote: note,
          employeeNumber,
          staffPin: pin,
        });
        toast.success('Dispute resolved');
      },
    });
  };

  const reject = () => {
    if (!detail?.dispute) return;
    setPinModal({
      title: 'Reject dispute',
      description:
        'Mark this dispute as not actionable. The player will see this status. Reason is required.',
      submitLabel: 'Reject',
      requireNote: true,
      noteLabel: 'Reason for rejection',
      destructive: true,
      action: async ({ pin, note }) => {
        await api.patch(`/admin/cage/disputes/${detail.dispute.id}`, {
          status: 'rejected',
          resolutionNote: note,
          employeeNumber,
          staffPin: pin,
        });
        toast.success('Dispute rejected');
      },
    });
  };

  // ----- render -----
  return (
    <div className="p-6 space-y-6">
      <ChipBankTabs />
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white flex items-center gap-2">
            <Flag className="text-amber-500" size={26} />
            Wallet Disputes
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            Player-reported wallet issues. Resolution requires a supervisor cage PIN.
          </p>
        </div>
        <button
          onClick={() => {
            fetchSummary();
            fetchList();
            if (selectedId) fetchDetail(selectedId);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-gray-200"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </header>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryCard label="Actionable" value={counts.actionable} accent="amber" />
        <SummaryCard label="Open" value={counts.open} accent="red" />
        <SummaryCard label="Reviewing" value={counts.reviewing} accent="blue" />
        <SummaryCard label="Resolved" value={counts.resolved} accent="green" />
        <SummaryCard label="Rejected" value={counts.rejected} accent="gray" />
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {STATUS_TABS.map((t) => {
            const active = statusFilter === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key || 'actionable'}
                onClick={() => setStatusFilter(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border ${
                  active
                    ? 'bg-amber-500 border-amber-500 text-black font-medium'
                    : 'bg-gray-800 border-gray-700 text-gray-300 hover:border-gray-500'
                }`}
              >
                {Icon && <Icon size={14} />}
                {t.label}
              </button>
            );
          })}
        </div>
        <div className="relative flex-1">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
          />
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by player name or email..."
            className="w-full pl-9 pr-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
          />
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* List */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl">
          <div className="px-4 py-3 border-b border-gray-700 flex items-center justify-between">
            <h2 className="text-white font-medium">
              {disputes.length} {disputes.length === 1 ? 'dispute' : 'disputes'}
            </h2>
            {loading && (
              <RefreshCw size={14} className="animate-spin text-amber-500" />
            )}
          </div>
          {!loading && disputes.length === 0 && (
            <div className="p-8 text-center text-gray-400">
              <Flag size={40} className="mx-auto mb-2 opacity-30" />
              <div>No disputes match this filter.</div>
            </div>
          )}
          <ul className="divide-y divide-gray-700/50 max-h-[70vh] overflow-y-auto">
            {disputes.map((d) => (
              <DisputeRow
                key={d.id}
                dispute={d}
                selected={d.id === selectedId}
                onClick={() => setSelectedId(d.id)}
              />
            ))}
          </ul>
        </div>

        {/* Detail */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl min-h-[400px]">
          {!detail && !detailLoading && (
            <div className="p-8 text-center text-gray-400">
              <Eye size={40} className="mx-auto mb-2 opacity-30" />
              <div>Select a dispute to see details.</div>
            </div>
          )}
          {detailLoading && (
            <div className="p-8 text-center text-gray-400">
              <RefreshCw size={20} className="mx-auto animate-spin text-amber-500" />
            </div>
          )}
          {detail && !detailLoading && (
            <DisputeDetail
              detail={detail}
              onStartReview={startReview}
              onResolve={resolve}
              onReject={reject}
            />
          )}
        </div>
      </div>

      {pinModal && (
        <PinModal
          modal={pinModal}
          employeeNumber={employeeNumber}
          setEmployeeNumber={setEmployeeNumber}
          onClose={() => setPinModal(null)}
          onSuccess={async () => {
            // Refresh after any mutation
            await fetchSummary();
            await fetchList();
            if (selectedId) await fetchDetail(selectedId);
          }}
        />
      )}
    </div>
  );
}

// ----------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------
function SummaryCard({ label, value, accent }) {
  const accentClasses = {
    amber: 'text-amber-400',
    red: 'text-red-400',
    blue: 'text-blue-400',
    green: 'text-green-400',
    gray: 'text-gray-300',
  };
  return (
    <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
      <div className="text-xs uppercase tracking-wide text-gray-400">{label}</div>
      <div className={`text-2xl font-semibold mt-1 ${accentClasses[accent]}`}>
        {value}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const config = {
    open: { color: 'bg-red-900/40 border-red-700/50 text-red-200', label: 'Open' },
    reviewing: {
      color: 'bg-blue-900/40 border-blue-700/50 text-blue-200',
      label: 'Reviewing',
    },
    resolved: {
      color: 'bg-green-900/40 border-green-700/50 text-green-200',
      label: 'Resolved',
    },
    rejected: {
      color: 'bg-gray-700/60 border-gray-600 text-gray-300',
      label: 'Rejected',
    },
  };
  const c = config[status] || config.open;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${c.color}`}>
      {c.label}
    </span>
  );
}

function PlayerAvatar({ user, size = 40 }) {
  const initials = useMemo(() => {
    if (!user) return '?';
    return ((user.firstName || '?').charAt(0) + (user.lastName || '').charAt(0))
      .toUpperCase();
  }, [user]);
  if (user?.profilePhotoUrl) {
    return (
      <img
        src={user.profilePhotoUrl}
        alt=""
        style={{ width: size, height: size }}
        className="rounded-full object-cover border border-gray-600"
        onError={(e) => {
          e.target.style.display = 'none';
        }}
      />
    );
  }
  return (
    <div
      style={{ width: size, height: size }}
      className="rounded-full bg-gray-700 border border-gray-600 flex items-center justify-center text-gray-300 font-semibold"
    >
      {initials}
    </div>
  );
}

function DisputeRow({ dispute, selected, onClick }) {
  return (
    <li
      onClick={onClick}
      className={`px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors ${
        selected ? 'bg-amber-900/20 border-l-4 border-amber-500' : 'hover:bg-gray-700/40'
      }`}
    >
      <PlayerAvatar user={dispute.user} size={40} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-white font-medium truncate">
            {dispute.user
              ? `${dispute.user.firstName} ${dispute.user.lastName}`
              : 'Unknown player'}
          </span>
          <StatusBadge status={dispute.status} />
        </div>
        <div className="text-sm text-gray-400 truncate">
          {dispute.reasonText.length > 80
            ? dispute.reasonText.slice(0, 80) + '…'
            : dispute.reasonText}
        </div>
        <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
          <Clock size={11} />
          {fmtAge(dispute.createdAt)}
          {dispute.ledgerEntry && (
            <>
              <span className="text-gray-600">·</span>
              <span>
                {dispute.ledgerEntry.direction === 'credit' ? '+' : '−'}
                {fmtCents(dispute.ledgerEntry.amountCents)}
              </span>
            </>
          )}
        </div>
      </div>
      <ChevronRight size={16} className="text-gray-500 flex-shrink-0" />
    </li>
  );
}

function DisputeDetail({ detail, onStartReview, onResolve, onReject }) {
  const d = detail.dispute;
  const audit = detail.recentAuditEvents || [];
  const canStartReview = d.status === 'open';
  const canResolve = d.status === 'open' || d.status === 'reviewing';
  const canReject = d.status === 'open' || d.status === 'reviewing';

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <PlayerAvatar user={d.user} size={56} />
          <div>
            <div className="text-white font-semibold text-lg">
              {d.user
                ? `${d.user.firstName} ${d.user.lastName}`
                : 'Unknown player'}
            </div>
            {d.user?.email && (
              <div className="text-sm text-gray-400">{d.user.email}</div>
            )}
            {d.user?.phone && (
              <div className="text-xs text-gray-500">{d.user.phone}</div>
            )}
          </div>
        </div>
        <StatusBadge status={d.status} />
      </div>

      <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
        <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">
          Player's report
        </div>
        <div className="text-white whitespace-pre-wrap">{d.reasonText}</div>
        <div className="text-xs text-gray-500 mt-2">
          Filed {fmtTime(d.createdAt)} · {fmtAge(d.createdAt)}
        </div>
      </div>

      {d.ledgerEntry && (
        <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-2">
            Disputed transaction
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Field label="Type" value={d.ledgerEntry.entryType} />
            <Field
              label="Amount"
              value={`${d.ledgerEntry.direction === 'credit' ? '+' : '−'}${fmtCents(
                d.ledgerEntry.amountCents
              )}`}
              emphasis
            />
            <Field
              label="Balance after"
              value={fmtCents(d.ledgerEntry.balanceAfterCents)}
            />
            <Field label="When" value={fmtTime(d.ledgerEntry.createdAt)} />
          </div>
          {d.ledgerEntry.note && (
            <div className="text-sm text-gray-300 mt-2 pt-2 border-t border-gray-700">
              <span className="text-gray-400">Note:</span> {d.ledgerEntry.note}
            </div>
          )}
        </div>
      )}

      {d.resolutionNote && (
        <div className="bg-gray-900/60 border border-gray-700 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wide text-gray-400 mb-1">
            Resolution
          </div>
          <div className="text-white whitespace-pre-wrap">{d.resolutionNote}</div>
          {d.resolvedAt && (
            <div className="text-xs text-gray-500 mt-2">
              {d.status === 'rejected' ? 'Rejected' : 'Resolved'} by{' '}
              {d.resolvedBy?.name || 'unknown'} · {fmtTime(d.resolvedAt)}
            </div>
          )}
        </div>
      )}

      {audit.length > 0 && (
        <details className="bg-gray-900/40 border border-gray-700 rounded-lg">
          <summary className="px-4 py-2 cursor-pointer text-sm text-gray-300">
            Wallet audit trail ({audit.length})
          </summary>
          <div className="px-4 pb-3">
            <ul className="text-xs text-gray-400 space-y-1">
              {audit.map((e) => (
                <li key={e.id}>
                  <span className="text-gray-500">{fmtTime(e.createdAt)}</span> ·{' '}
                  <span className="text-gray-300">{e.eventType}</span>
                </li>
              ))}
            </ul>
          </div>
        </details>
      )}

      {/* Actions */}
      {(canStartReview || canResolve || canReject) && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-700">
          {canStartReview && (
            <button
              onClick={onStartReview}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
            >
              <Eye size={16} />
              Start review
            </button>
          )}
          {canResolve && (
            <button
              onClick={onResolve}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
            >
              <CheckCircle size={16} />
              Resolve
            </button>
          )}
          {canReject && (
            <button
              onClick={onReject}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-200 rounded-lg text-sm font-medium border border-gray-600"
            >
              <XCircle size={16} />
              Reject
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, emphasis }) {
  return (
    <div>
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div
        className={`text-sm ${emphasis ? 'text-amber-400 font-semibold' : 'text-white'}`}
      >
        {value}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// PinModal — supervisor PIN + optional resolution note
// ----------------------------------------------------------------
function PinModal({ modal, employeeNumber, setEmployeeNumber, onClose, onSuccess }) {
  const [pin, setPin] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!employeeNumber) {
      return toast.error('Enter your employee number');
    }
    if (!pin) {
      return toast.error('PIN required');
    }
    if (modal.requireNote && !note.trim()) {
      return toast.error(`${modal.noteLabel || 'Note'} is required`);
    }
    setSubmitting(true);
    try {
      await modal.action({ pin, note: note.trim() });
      await onSuccess();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-800 border border-gray-700 rounded-xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">{modal.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>
        {modal.description && (
          <p className="text-gray-400 text-sm">{modal.description}</p>
        )}

        {!employeeNumber && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              Employee #
            </label>
            <input
              type="text"
              value={employeeNumber}
              onChange={(e) => setEmployeeNumber(e.target.value)}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
              placeholder="Your supervisor employee number"
              autoFocus
            />
          </div>
        )}

        {(modal.requireNote || modal.noteLabel) && (
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
              {modal.noteLabel || 'Note'}
              {modal.requireNote && <span className="text-red-400 ml-1">*</span>}
            </label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
              placeholder={
                modal.requireNote
                  ? 'Required — describe what was investigated and the outcome'
                  : 'Optional'
              }
            />
          </div>
        )}

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-400 mb-1">
            <KeyRound size={12} className="inline mr-1" />
            Supervisor PIN
          </label>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
            placeholder="••••"
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-400 hover:text-white"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium disabled:opacity-50 ${
              modal.destructive
                ? 'bg-red-600 hover:bg-red-700 text-white'
                : 'bg-amber-500 hover:bg-amber-600 text-black'
            }`}
          >
            {submitting ? (
              <RefreshCw className="animate-spin" size={16} />
            ) : (
              <CheckCircle size={16} />
            )}
            {modal.submitLabel || 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}
